import type { Node as PmNode } from "prosemirror-model";
import type { EditorView } from "prosemirror-view";
import type { Msg } from "../types";
import { cardTitle } from "../card-title";
import { cardContent, watchCard } from "./cards";
import { cardMenuButton } from "./card-link-menu";
import { showLinkPopover, hideLinkPopover } from "./link-popover";

// An inline link shows the title of another card, and a click opens that card.
// A link in a live editor also has the display button and a preview on hover. A
// section is `CardSectionView`.
export class CardLinkNodeView {
  dom: HTMLElement;
  private uuid: string;
  private slotEl: HTMLElement;
  private unwatch: () => void;

  constructor(
    node: PmNode,
    view: EditorView,
    getPos: () => number | undefined,
    private dispatch: (msg: Msg) => void,
    private live: boolean
  ) {
    this.uuid = node.attrs["uuid"] as string;

    const a = document.createElement("a");
    a.className = "chip chip--small card-link";
    a.setAttribute("data-card-link", this.uuid);
    a.contentEditable = "false";
    a.addEventListener("mousedown", (e) => {
      e.preventDefault();
      hideLinkPopover();
      this.dispatch({ type: "NavigateTo", id: this.uuid });
    });

    const slot = document.createElement("span");
    slot.className = "card-link__label";
    this.slotEl = slot;
    a.appendChild(slot);
    if (live) {
      a.addEventListener("mouseenter", () => this.showHover(a));
      a.addEventListener("mouseleave", () => hideLinkPopover());
      a.appendChild(cardMenuButton({ view, getPos, uuid: this.uuid, current: "inline" }));
    }
    this.dom = a;

    this.unwatch = watchCard(this.uuid, dispatch, () => this.renderLabel());
  }

  update(node: PmNode): boolean {
    return node.attrs["uuid"] === this.uuid;
  }

  ignoreMutation(): boolean {
    return true;
  }

  destroy(): void {
    this.unwatch();
    // Only a live link opens the popover. A link in a preview can be in the
    // popover, and a hide from it would destroy the popover again.
    if (this.live) hideLinkPopover();
  }

  private renderLabel(): void {
    const content = cardContent(this.uuid);
    const label = content === undefined ? "…" : cardTitle(content);
    if (this.slotEl.textContent !== label) this.slotEl.textContent = label;
  }

  private showHover(anchor: HTMLElement): void {
    const content = cardContent(this.uuid);
    if (content) showLinkPopover(anchor, content, 1, this.dispatch);
  }
}
